import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import Button from "@/ui/Button";
import { ContactInput } from "@/ui/forms/inputs/ContactInput";
import { PasswordInput } from "@/ui/forms/inputs/PasswordInput";
import { AlertCircle } from "lucide-react";
import { loginSchema, type LoginFormData } from "@/schemas/formSchemas";

export default function LoginForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { login } = useAuth();

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: { contactNo: "", password: "" },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await login(`+91${data.contactNo}`, data.password);
    } catch {
      setApiError("Invalid contact number or password");
    } finally {
      setIsLoading(false);
    }
  };

  const validationError = form.formState.errors.contactNo?.message || form.formState.errors.password?.message
  const displayError = validationError || apiError

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
      <div className="flex flex-col gap-5">
        <ContactInput
          name="contactNo"
          label="Contact number"
          required
          disabled={isLoading}
          register={form.register("contactNo")}
        />

        <PasswordInput
          name="password"
          label="Password"
          placeholder="••••••••"
          required
          disabled={isLoading}
          register={form.register("password")}
        />

        {displayError && (
          <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-[var(--status-error-bg)] border border-[var(--status-error-border)] animate-[slideDown_0.15s_ease-out]">
            <AlertCircle className="h-3.5 w-3.5 text-[var(--status-error-text)] shrink-0 mt-0.5" />
            <span className="text-xs text-[var(--status-error-text)]">{displayError}</span>
          </div>
        )}
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        disabled={isLoading}
        loading={isLoading}
        fullWidth
        className="mt-4"
      >
        Sign in
      </Button>
    </form>
  );
}
